<template>
  <section class="ma-4 ma-md-8">
    <t-page-title
      title="Pré Impressão"
      :on-add="can('create', 'pre-print') ? () => $router.push({ name: 'create-pre-print' }) : undefined"
      :on-print="can('print', 'pre-print') ? () => testPrinter() : undefined"
    />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.sector`]="{ item }">
            <span>
              {{ item.batch.ticket_type.sector.name }} ({{ item.batch.ticket_type.name }}<span v-if="item.courtesy_name"> - {{ item.courtesy_name }}</span>)
            </span>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span> {{ item.created_at ? DateTime.fromSQL(item.created_at).toFormat('dd/MM/yyyy HH:mm:ss') : '???' }}</span>
          </template>
          <template #[`item.price`]="{ item }">
            <span> {{ $formatters.currency(parseFloat(item.batch.price)) }}</span>
          </template>
          <template #[`item.printed_at`]="{ item }">
            <span> {{ item.printed_at ? DateTime.fromSQL(item.printed_at).toFormat('dd/MM/yyyy HH:mm:ss') : '' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import { Ref, ref } from 'vue'
import { DataTableFilter, DataTableHeader } from '@/types/DataTable'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useProducerStore } from '@/stores/producer'
import { DateTime, Interval } from 'luxon'
import { Resources } from '@/plugins/permissions/typing'
import { useLoader } from '@/stores/loader'
import { useDialog } from '@/stores/dialog'
import { useSnackBar } from '@/stores/snackbar'
import { useAbility } from '@casl/vue'
import { DefaultDataTableActions } from '@/types/DataTable'
import PrePrint from '@/types/PrePrint'
import TPageTitle from '@/components/TPageTitle.vue'
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import PrePrintService from '@/services/PrePrintService'
import { useSectorFilter } from '@/helpers/reports/sectorFilter'
import { useTicketTypeFilter } from '@/helpers/reports/ticketTypeFilter'
import { useTemplateRef } from 'vue'

const { can } = useAbility()
const snackbar = useSnackBar()
const dialog = useDialog()
const loader = useLoader()

const resource: Resources = 'pre-print'

const datatable = useTemplateRef<DataTableType>('datatable')

const service = new PrePrintService()

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Data criação', value: 'created_at', key: 'created_at', sortable: false },
  { title: 'Setor', value: 'batch.ticket_type', key: 'sector', sortable: false },
  { title: 'Lote', value: 'batch.number', key: 'batch', sortable: false, align: 'center' },
  { title: 'Valor', value: 'batch.price', key: 'price', sortable: false },
  { title: 'Quantidade', value: 'quantity', key: 'quantity', sortable: false, align: 'center' },
  { title: 'Data impressão', value: 'printed_at', key: 'printed_at', sortable: false },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
]

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  label: 'Evento',
  field: 'event_id',
  type: 'select',
  queryParams: {
    // It was asked to fetch only events that ended after -7 days
    end_between: Interval.fromDateTimes(
      DateTime.now().minus({ days: 7 }),
      DateTime.now().plus({ years: 5 }),
    ).toISO(),
  },
})

const filters: Ref<DataTableFilter[]> = ref([
  eventFilter,
  useSectorFilter({
    event: eventFilter.model,
    label: 'Setor',
    field: 'sector_id',
  }),
  useTicketTypeFilter({
    event: eventFilter.model,
    label: 'Tipo de ingresso',
    field: 'ticket_type_id',
  }),
])

const actions: DefaultDataTableActions<PrePrint> = {
  print: {
    callback: async (item: PrePrint) => {
      const confirm = await dialog.open({
        title: 'Impressão',
        message: 'Você tem certeza que deseja imprimir a pré impressão? Esta ação é irreversível.',
      })

      if (confirm) service.print(item.id)
    },
    condition: prePrint => can('print', 'pre-print') && !prePrint.printed,
    icon: 'fa-solid fa-print',
    color: 'gray',
  },
  destroy: {
    callback: item => destroy(item),
    condition: () => can('delete', 'pre-print'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, _filters: string, per_page?: number) {
  try {
    loader.increment()
    return await service.index(page, _filters, per_page)
  } finally {
    loader.decrement()
  }
}

async function destroy(model: PrePrint) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir a pré impressão? Esta ação é irreversível.',
  })

  if (!confirm) return

  try {
    loader.increment()

    await service.destroy(model)

    datatable.value?.search()

    snackbar.show(
      'Pré impressão removida com sucesso',
      'success',
    )
  } finally {
    loader.decrement()
  }
}

async function testPrinter() {
  const confirm = await dialog.open({
    title: 'Impressão',
    message: 'Você tem certeza que deseja realizar um teste de impressão? Esta ação é irreversível.',
  })

  if (confirm) {
    await service.testPrinter()
  }
}

</script>
